import React, { useState } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Chip, Container, CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabaseClient';
import { Unidade, Obra } from '../services/types';
import { getObras } from '../services/api';
import UnidadeFormModal from '../components/UnidadeFormModal';

const UnidadesPage: React.FC = () => {
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUnidade, setSelectedUnidade] = useState<Unidade | null>(null);

    const { data: obras = [] } = useQuery<Obra[]>({
        queryKey: ['obras-list-unidades'],
        queryFn: getObras,
    });

    const { data: unidades, isLoading, error } = useQuery<Unidade[]>({
        queryKey: ['unidades'],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_unidades_admin');
            if (error) throw error;
            return data || [];
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            const { error } = await supabase.rpc('delete_unidade', { p_id: id });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['unidades'] });
        },
        onError: (error: any) => {
            alert('Erro ao excluir unidade: ' + error.message);
        }
    });

    const handleCreate = () => {
        setSelectedUnidade(null);
        setIsModalOpen(true);
    };

    const handleEdit = (unidade: Unidade) => {
        setSelectedUnidade(unidade);
        setIsModalOpen(true);
    };

    const handleDelete = (id: number) => {
        if (window.confirm('Tem certeza que deseja excluir esta unidade?')) {
            deleteMutation.mutate(id);
        }
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (error) return <Typography color="error">Erro ao carregar unidades: {(error as any).message}</Typography>;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Gestão de Unidades
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
                    Nova Unidade
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Identificação</TableCell>
                            <TableCell>Obra</TableCell>
                            <TableCell>Localização</TableCell>
                            <TableCell align="right">Área Construída</TableCell>
                            <TableCell align="right">Área Não Constr.</TableCell>
                            <TableCell align="right">Área Privativa Total</TableCell>
                            <TableCell align="center">Vagas</TableCell>
                            <TableCell align="right">Valor</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {unidades?.map((unidade) => {
                            const obraVinculada = obras.find(o => o.id === unidade.obra_id);
                            const areaPrivativa =
                                unidade.area_privativa_total ??
                                unidade.area ??
                                (unidade.area_construida != null || unidade.area_nao_construida != null
                                    ? (Number(unidade.area_construida) || 0) + (Number(unidade.area_nao_construida) || 0)
                                    : null);
                            return (
                            <TableRow key={unidade.id}>
                                <TableCell>
                                    <Typography variant="subtitle2">{unidade.identificacao}</Typography>
                                    <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                                        {unidade.descricao}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {obraVinculada ? (
                                        <Chip label={obraVinculada.nome} size="small" variant="outlined" color="primary" />
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">Avulsa</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {unidade.municipio && unidade.estado ? `${unidade.municipio}/${unidade.estado}` : '-'}
                                    {unidade.bairro && <Typography variant="caption" display="block">{unidade.bairro}</Typography>}
                                </TableCell>
                                <TableCell align="right">
                                    {unidade.area_construida != null ? `${Number(unidade.area_construida).toLocaleString('pt-BR')} m²` : '-'}
                                </TableCell>
                                <TableCell align="right">
                                    {unidade.area_nao_construida != null ? `${Number(unidade.area_nao_construida).toLocaleString('pt-BR')} m²` : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    {areaPrivativa != null ? `${Number(areaPrivativa).toLocaleString('pt-BR')} m²` : '-'}
                                </TableCell>
                                <TableCell align="center">
                                    {unidade.vagas != null ? unidade.vagas : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(unidade.valor)}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={unidade.status}
                                        color={unidade.status === 'Disponível' ? 'success' : unidade.status === 'Vendida' ? 'error' : 'warning'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleEdit(unidade)} color="primary">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(unidade.id)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                            );
                        })}
                        {unidades?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    Nenhuma unidade cadastrada.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <UnidadeFormModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                unidade={selectedUnidade}
            />
        </Container>
    );
};

export default UnidadesPage;
